/*
 * Copyright (c) 2021. FINN Sp. z o.o.
 */

package pl.finn.forms.generator

import io.kvision.core.Component
import io.kvision.core.StringPair
import io.kvision.core.onEvent
import io.kvision.form.check.RadioGroup
import io.kvision.html.Div
import io.kvision.panel.SimplePanel
import io.kvision.panel.Tab
import io.kvision.panel.TabPanel
import io.kvision.panel.TabPosition
import org.w3c.dom.Document
import pl.finn.forms.enums.BindedAs
import pl.finn.forms.enums.ControlType
import pl.finn.forms.enums.ViewType
import pl.finn.forms.model.Components
import pl.finn.forms.model.ComponentsGroup
import pl.finn.forms.model.DocumentTreeNode
import pl.finn.forms.model.Fieldset
import pl.finn.forms.utils.FormReadingUtils
import pl.finn.forms.utils.form.FormBuilder

class ElementMatcher(reference: FormBuilder<DocumentTreeNode>, isPillsTabRequire: Boolean) {
  private val formReference = reference
  private val components = Components(this, formReference)
  private val componentsGroup = ComponentsGroup(this, formReference)
  private val fieldset = Fieldset(formReference)
  private var previousElement: DocumentTreeNode? = null
  private var currentElement: DocumentTreeNode? = null
  private var tabParent = HashMap<String?, DocumentTreeNode?>()
  private var tabParentSize = 0
  private var level = 0
  private var complexLevel = 0
  private var isPillsTabPanel = isPillsTabRequire
  var usedElementsList = ArrayList<DocumentTreeNode?>()
  val tabLevel = TabPanel(TabPosition.TOP, className = "nav-pills") { id = "pills-tabs" }

  suspend fun matchElement(element: DocumentTreeNode, parentElement: DocumentTreeNode?): Component? {
    if (usedElementsList.contains(element) || element.metaModel?.hidden == true) return null
    val tabDiv = if (isPillsTabPanel && (tabParent[parentElement?.name] != null || level < 2)) {
      if (parentElement != null && tabParent[parentElement.name] == null || (tabParent.isEmpty() && level == 0)) {
        tabParent[parentElement?.name] = parentElement
        tabParentSize += parentElement?.children?.size!!
      } else if (element.children.size > 0 && element.children[0]?.metaModel?.viewType?.equals(ViewType.COMPLEX) == true && tabParentSize < 8) {
        tabParent[element.name] = element
        tabParentSize += element.children.size
      }
      tabLevel
    } else null
    with(element.metaModel?.viewType) {
      var component: Component? = when {
        (element.label.isNullOrEmpty() && element.metaModel?.label.isNullOrEmpty() &&
            element.children.size == 1 && element.children[0]?.name.isNullOrEmpty()) -> Div()
        this?.equals(ViewType.COMPLEX) == true -> matchComplexElement(element, parentElement)
        this?.equals(ViewType.SIMPLE) == true -> matchElementByType(element, parentElement)
        this?.equals(ViewType.SIMPLE_CONTENT) == true -> matchElementByType(element, parentElement)
        this?.equals(ViewType.ENUMERATION) == true -> components.createSelect(element, parentElement)
        this?.equals(ViewType.ENUMERATION_CONVERTED) == true -> components.createSelect(element, parentElement)
        this?.equals(ViewType.FIXED) == true -> components.checkIfItShouldBeCheckboxOrLabel(element)
        this?.equals(ViewType.FIXED_CONVERTED) == true -> components.createCheckBox(element)
        else -> matchElementByType(element, parentElement)
      }
      if (tabDiv != null && parentElement?.metaModel?.choiceView == null && component != null) {
        fieldset.createPillsTab(element, component).let { tabDiv.add(it) }
        component = null
      }
      return component
    }
  }

  private suspend fun matchComplexElement(element: DocumentTreeNode, parentElement: DocumentTreeNode?): Component? {
    if (level != complexLevel || parentElement != null) {
      previousElement = parentElement ?: currentElement
      complexLevel = level
    }
    currentElement = element
    with(element) {
      val component: Component? = when {
        this.name?.matches("Data") == true && this.children.isEmpty() -> components.createDateTime(element)
        this.metaModel?.particleView != null && this.metaModel?.particleView?.get(0)?.isCheckbox == true -> {
          if (this.metaModel?.choiceView != null) {
            componentsGroup.createElementsGroup(element, parentElement)
          } else {
            val nestedElementsForm = if (this.metaModel?.controlType?.name.equals("ANNEX") || this.typeName.equals("TZalacznik")) {
              linkedMapOf<DocumentTreeNode, Any>(element to componentsGroup.addAttachments(element, formReference))
            } else matchNestedElements(element)
            if (nestedElementsForm.size == 0) return null
            componentsGroup.createCollapseCheckbox(element, nestedElementsForm)
          }
        }
        this.metaModel?.choiceView != null && this.metaModel?.choiceView?.get(0)?.labels?.size!! > 0 &&
            this.children.size > 1 -> {
          components.createChoiceElement(element, null, parentElement)
        }
        this.metaModel?.controlType?.name.equals("ANNEX") ->
          componentsGroup.addAttachments(element, formReference)
        !(this.name.equals("Dokument") || this.name.equals("TrescDokumentu")) &&
            !(this.label.isNullOrEmpty() && this.metaModel?.label.isNullOrEmpty()) -> {
          val nestedElementsForm = matchNestedElements(element)
          if (nestedElementsForm.size == 0) return null
          fieldset.createFieldset(element, nestedElementsForm, previousElement?.children!!)
        }
        else -> {
          val nestedElementsForm = matchNestedElements(element)
          if (nestedElementsForm.size == 0) return null
          componentsGroup.createDivGroupElement(element, nestedElementsForm, parentElement)
        }
      }
      return component
    }
  }

  private suspend fun matchElementByType(
    element: DocumentTreeNode,
    parentElement: DocumentTreeNode?
  ): Component? {
    with(element.typeName) {
      val div = Div()
      val component = when {
        element.metaModel?.isElementRequired == false && parentElement?.name.equals("Naglowek") -> null
        element.metaModel?.controlType == ControlType.CZAS || element.metaModel?.controlType == ControlType.DATE ||
            element.typeName?.matches("TData") == true -> components.createDateTime(element)
        element.typeName?.matches("boolean") == true -> components.createCheckBox(element)
        this?.matches("Tekst10Typ|Tekst65Typ|TytulTyp") == true || element.metaModel?.inputSize!! < 1000 ->
          components.createInput(element, parentElement)
        this?.matches("Tekst200Typ|Tekst1000Typ|Tekst2000Typ|Tekst5000Typ|Tekst10000Typ") == true ||
            element.metaModel?.inputSize!! > 999 -> components.createTextArea(element, parentElement)
        else -> return null
      }
      component.let { if (it != null) { div.add(it) } }
      if (element.children.isNotEmpty()) {
        matchNestedElements(element).forEach {
          div.add(it.value as Component)
        }
      } else if (!element.metaModel?.choiceView.isNullOrEmpty()) {
        div.add(components.createSimpleChoiceElement(element, parentElement))
      }
      return div
    }
  }

  private suspend fun matchNestedElements(element: DocumentTreeNode): LinkedHashMap<DocumentTreeNode, Any> {
    val nestedElements = LinkedHashMap<DocumentTreeNode, Any>()
    level++
    var skipSiblings = 0
    for (el in element.children) {
      if (el != null && skipSiblings < 1) {
        matchElement(el, element)?.let { nestedElements.put(el, it) }
        if (el.metaModel?.particleView != null && el.metaModel?.choiceView != null &&
          el.metaModel?.particleView!![0]?.bindedAs == BindedAs.NEXT_SIBLING && FormReadingUtils.isNextSiblingChoice(el)) {
          skipSiblings = el.metaModel?.choiceView!![0]?.labels?.size ?: 0
          var index = element.children.indexOf(el) + 1
          while (element.children.get(index)?.name.equals(el.name)) {
            skipSiblings++
            index++
          }
        }
      } else {
        skipSiblings--
      }
    }
    level--
    return nestedElements
  }
}